import { useEffect, useState } from "react";
import { Clock, ArrowUpDown, ArrowDownCircle, LineChart, ArrowUpCircle, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading";
import axiosInstance from "../axiosConfig";
import { handleError } from "../services/handleError";
import { showToast } from "../components/Toast";

export default function Estoque() {
    const [produtos, setProdutos] = useState([]);
    const [historicoMovimentacao, setHistoricoMovimentacao] = useState([]);
    const [modalProduto, setModalProduto] = useState(null);
    const [modalTipo, setModalTipo] = useState(null);
    const [tipoMovimentacao, setTipoMovimentacao] = useState("Entrada");
    const [quantidadeMovimentacao, setQuantidadeMovimentacao] = useState(0);
    const [observacao, setObservacao] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        buscarEstoque();
    }, []);

    async function buscarEstoque() {
        setLoading(true);

        try {
            const response = await axiosInstance.get("Estoque");

            setProdutos(response.data);
        } catch (error) {
            handleError(error, "Erro ao buscar estoque.");
        } finally {
            setLoading(false);
        }
    }

    async function buscarHistoricoMovimentacao(estoqueId) {
        setLoading(true);

        try {
            const response = await axiosInstance.get("MovimentacaoEstoque/ultimas/" + estoqueId);

            setHistoricoMovimentacao(response.data);
        } catch (error) {
            handleError(error, "Erro ao buscar estoque.");
        } finally {
            setLoading(false);
        }
    }

    const abrirModalMovimentacao = (produto) => {
        setModalProduto(produto);
        setModalTipo("movimentacao");
        setTipoMovimentacao("Entrada");
        setQuantidadeMovimentacao(0);
        setObservacao("");
    };

    const abrirModalHistorico = (produto) => {
        buscarHistoricoMovimentacao(produto.id);
        setModalTipo("historico");
        setModalProduto(produto);
    };

    const fecharModal = () => {
        setModalProduto(null);
        setModalTipo(null);
    };

    const salvarMovimentacao = async () => {
        if (!quantidadeMovimentacao || quantidadeMovimentacao <= 0) return;
        setLoading(true);

        try {
            const response = await axiosInstance.post("MovimentacaoEstoque", {
                estoqueId: modalProduto.id,
                tipo: tipoMovimentacao.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
                quantidade: quantidadeMovimentacao,
                descricao: observacao.trim(),
            });

            const novaQuantidade =
                tipoMovimentacao === "Entrada"
                    ? modalProduto.quantidade + quantidadeMovimentacao
                    : modalProduto.quantidade - quantidadeMovimentacao;
                
            const atualizados = produtos.map((p) =>
                p.id === modalProduto.id
                    ? { ...p, quantidade: novaQuantidade, em_alerta: response.data.emAlerta}
                    : p
            );

            setProdutos(atualizados);
            fecharModal();
            showToast("Sucesso!", "success", 3000, "Movimentação salva com sucesso!");
        } catch (error) {
            handleError(error, "Erro ao atualizar movimentação.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="p-5">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Controle de Estoque</h1>
                <button
                    onClick={() => {
                        navigate('/gestaoinsumos');
                    }}
                    className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Settings size={16} />
                    Gerenciar Insumos
                </button>
            </div>

            <div className="overflow-x-auto rounded-lg shadow border">
                <table className="w-full text-sm text-left border-collapse">
                    <thead className="bg-gray-100 text-gray-700">
                        <tr>
                            <th className="px-4 py-3">Produto</th>
                            <th className="px-4 py-3">Quantidade</th>
                            <th className="px-4 py-3">Unidade</th>
                            <th className="px-4 py-3">Alerta</th>
                            <th className="px-4 py-3 text-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {produtos.map((produto, idx) => (
                            <tr
                                key={produto.id}
                                className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} border-t`}
                            >
                                <td className="px-4 py-3 font-medium">{produto.nome}</td>
                                <td className={`px-4 py-3 font-semibold ${produto.em_alerta ? "text-red-600" : "text-gray-800"}`}>
                                    {produto.quantidade}
                                </td>
                                <td className="px-4 py-3">{produto.unidade}</td>
                                <td className="px-4 py-3">
                                    {produto.em_alerta ? (
                                        <span className="text-red-600 font-semibold">⚠ Baixo</span>
                                    ) : "-"}
                                </td>
                                <td className="px-4 py-3 flex justify-center gap-4">
                                    <button
                                        onClick={() => abrirModalMovimentacao(produto)}
                                        className="text-green-600 hover:text-green-800 transition"
                                        title="Movimentar estoque"
                                    >
                                        <ArrowUpDown className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => abrirModalHistorico(produto)}
                                        className="text-gray-600 hover:text-gray-800 transition"
                                        title="Ver histórico"
                                    >
                                        <Clock className="w-5 h-5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modalProduto && modalTipo === "movimentacao" && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Movimentar Estoque — {modalProduto.nome}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Estoque atual: <strong>{modalProduto.quantidade} {modalProduto.unidade}</strong>
                            </p>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Tipo de movimentação */}
                            <div>
                                <span className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo de movimentação
                                </span>
                                <div className="flex gap-3">
                                    <button
                                        className={`flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-lg font-semibold border transition-all duration-200 ${tipoMovimentacao === "Entrada"
                                            ? "bg-green-600 text-white border-green-600"
                                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                                            }`}
                                        onClick={() => setTipoMovimentacao("Entrada")}
                                    >
                                        <ArrowDownCircle className="w-4 h-4" />
                                        Entrada
                                    </button>

                                    <button
                                        className={`flex items-center justify-center gap-2 flex-1 px-4 py-2 rounded-lg font-semibold border transition-all duration-200 ${tipoMovimentacao === "Saída"
                                            ? "bg-red-600 text-white border-red-600"
                                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                                            }`}
                                        onClick={() => setTipoMovimentacao("Saída")}
                                    >
                                        <ArrowUpCircle className="w-4 h-4" />
                                        Saída
                                    </button>
                                </div>
                            </div>

                            {/* Quantidade */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quantidade ({modalProduto.unidade})
                                </label>
                                <input
                                    type="number"
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-blue-200"
                                    value={quantidadeMovimentacao}
                                    onChange={(e) => setQuantidadeMovimentacao(Number(e.target.value))}
                                    min={1}
                                />
                            </div>

                            {/* Observação */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Observação <span className="text-gray-400">(opcional)</span>
                                </label>
                                <textarea
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-blue-200 resize-none"
                                    rows={2}
                                    value={observacao}
                                    onChange={(e) => setObservacao(e.target.value)}
                                    placeholder={
                                        tipoMovimentacao === "Entrada"
                                            ? "Ex: compra, reposição, devolução do cliente..."
                                            : "Ex: consumo interno, perda, devolução ao fornecedor..."
                                    }
                                />
                            </div>
                        </div>

                        <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
                            <button
                                onClick={fecharModal}
                                className="px-4 py-2 text-sm rounded-md border text-gray-700 hover:bg-gray-100"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={salvarMovimentacao}
                                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modalProduto && modalTipo === "historico" && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-fade-in overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h2 className="text-xl font-semibold text-gray-800">
                                Histórico - {modalProduto.nome}
                            </h2>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto divide-y">
                            {historicoMovimentacao.length === 0 ? (
                                <p className="text-gray-500 text-center py-8 text-sm">
                                    Nenhuma movimentação registrada.
                                </p>
                            ) : (
                                historicoMovimentacao.map((item, i) => {
                                    const isEntrada = item.tipo === "Entrada";
                                    const isVenda = item.automatica;

                                    const Icon = isEntrada
                                        ? ArrowDownCircle
                                        : isVenda
                                            ? LineChart
                                            : ArrowUpCircle;

                                    const iconBg = isEntrada
                                        ? "bg-green-100 text-green-600"
                                        : isVenda
                                            ? "bg-blue-100 text-blue-600"
                                            : "bg-red-100 text-red-600";

                                    const badgeColor = isEntrada
                                        ? "bg-green-50 text-green-700"
                                        : isVenda
                                            ? "bg-blue-50 text-blue-700"
                                            : "bg-red-50 text-red-700";

                                    const dataFormatada = new Intl.DateTimeFormat("pt-BR", {
                                        dateStyle: "short",
                                        timeStyle: "short",
                                    }).format(new Date(item.data));

                                    return (
                                        <div key={i} className="flex items-start gap-3 px-5 py-4">
                                            {/* Ícone */}
                                            <div className={`p-2 rounded-full ${iconBg}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>

                                            {/* Detalhes */}
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {item.tipo} de{" "}
                                                        <strong>
                                                            {item.quantidade} {modalProduto.unidade}
                                                        </strong>
                                                    </p>
                                                    <span className="text-xs text-gray-400">
                                                        {dataFormatada}
                                                    </span>
                                                </div>

                                                {item.descricao && (
                                                    <p className="text-xs text-gray-500 italic mt-1">
                                                        “{item.descricao}”
                                                    </p>
                                                )}

                                                {/* Badge */}
                                                {isVenda && (
                                                    <span className={`text-[10px] px-2 py-0.5 rounded mt-1 inline-block ${badgeColor}`}>
                                                        Saída automática de venda
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="border-t px-6 py-4 flex justify-end bg-gray-50">
                            <button
                                onClick={fecharModal}
                                className="px-4 py-2 text-sm rounded-md border text-gray-700 hover:bg-gray-100"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}